import { IntlShape } from 'react-intl';
import { z } from 'zod';

export const emailSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.string().email(formatMessage({ defaultMessage: 'Invalid email address', id: 'validator.email.invalid' }));
export const passwordSchema = (formatMessage: IntlShape['formatMessage']) =>
  z
    .string()
    .min(
      8,
      formatMessage({ defaultMessage: 'Password must be at least 8 characters', id: 'validator.password.minLength' })
    );
export const companySchema = (formatMessage: IntlShape['formatMessage']) =>
  z.string().min(2, formatMessage({ defaultMessage: 'Company is required', id: 'validator.company.required' }));
export const countrySchema = (formatMessage: IntlShape['formatMessage']) =>
  z.string().length(2, formatMessage({ defaultMessage: 'Country is required', id: 'validator.country.required' }));
export const acceptTermsSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.boolean().refine(value => value === true, {
    message: formatMessage({
      defaultMessage: 'You must accept the terms and conditions',
      id: 'validator.acceptTerms.required'
    })
  });

const dummyFormatMessage: IntlShape['formatMessage'] = ({
  defaultMessage,
  id
}: {
  defaultMessage?: string;
  id: string;
}) => defaultMessage || id;

export const getLoginSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.object({
    email: emailSchema(formatMessage),
    password: passwordSchema(formatMessage)
  });

const loginSchema = getLoginSchema(dummyFormatMessage);

export const getForgotPasswordSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.object({
    email: emailSchema(formatMessage)
  });

const forgotPasswordSchema = getForgotPasswordSchema(dummyFormatMessage);

export const getSignupSchema = (formatMessage: IntlShape['formatMessage'] = dummyFormatMessage) =>
  z
    .object({
      acceptedTerms: acceptTermsSchema(formatMessage),
      companyName: companySchema(formatMessage),
      country: countrySchema(formatMessage),
      email: emailSchema(formatMessage),
      password: passwordSchema(formatMessage),
      repeatPassword: z
        .string()
        .min(8, formatMessage({ defaultMessage: 'Repeat your password', id: 'signup.validator.repeatPassword' }))
    })
    .refine(data => data.password === data.repeatPassword, {
      message: formatMessage({ defaultMessage: 'Passwords do not match', id: 'signup.validator.passwordMismatch' }),
      path: ['repeatPassword']
    });

const signUpSchema = getSignupSchema();

export const getResetPasswordSchema = (formatMessage: IntlShape['formatMessage'] = dummyFormatMessage) =>
  z
    .object({
      password: passwordSchema(formatMessage),
      repeatPassword: z
        .string()
        .min(8, formatMessage({ defaultMessage: 'Repeat your password', id: 'resetPassword.validator.repeatPassword' }))
    })
    .refine(data => data.password === data.repeatPassword, {
      message: formatMessage({
        defaultMessage: 'Passwords do not match',
        id: 'resetPassword.validator.passwordMismatch'
      }),
      path: ['repeatPassword']
    });

const resetPasswordSchema = getResetPasswordSchema();

export type LoginFormValues = z.infer<typeof loginSchema>;
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;
export type SignupFormValues = z.infer<typeof signUpSchema>;
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
